import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTree, FaTools, FaLeaf, FaUsers, FaChartLine } from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const [stats, setStats] = useState([
    { value: 5000, label: 'Tools Available', icon: <FaTools className="text-green-500" /> },
    { value: 42, label: 'City Parks Served', icon: <FaTree className="text-green-500" /> },
    { value: 120, label: 'Team Members', icon: <FaUsers className="text-green-500" /> },
    { value: 98, label: 'Projects Completed', icon: <FaLeaf className="text-green-500" /> }
  ]);

  // Animated counter effect
  useEffect(() => {
    stats.forEach((stat, index) => {
      const target = stat.value;
      const duration = 2000;
      const step = target / (duration / 16);
      let current = 0;

      const timer = setInterval(() => {
        current += step;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setStats(prev => {
          const newStats = [...prev];
          newStats[index].displayValue = Math.floor(current);
          return newStats;
        });
      }, 16);

      return () => clearInterval(timer);
    });
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Intersection observer for scroll animations
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-10 mt-20 sm:px-6 lg:px-8">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16"
      >
        <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
          <span className="block text-green-600">Street Horticulture</span>
          <span className="block">Inventory System</span>
        </h1>
        <p className="mt-5 max-w-xl mx-auto text-xl text-gray-500">
          Empowering Vancouver's green spaces through efficient tool management
        </p>
      </motion.div>

      {/* Mission Section */}
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-7xl mx-auto bg-white rounded-xl shadow-md overflow-hidden mb-16"
      >
        <div className="md:flex">
          <motion.div 
            variants={itemVariants}
            className="md:flex-shrink-0 bg-green-600 md:w-1/3 flex items-center justify-center p-12"
          >
            <FaLeaf className="h-32 w-32 text-white" />
          </motion.div>
          <motion.div 
            variants={itemVariants}
            className="p-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="mt-2 text-gray-600 text-lg">
              The Vancouver Street Horticulture Department maintains over 500 acres of urban green space. 
              Our inventory system ensures every team member has access to the right tools at the right time, 
              helping keep our city beautiful and sustainable.
            </p>
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Key Features:</h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="bg-green-100 text-green-800 p-1 rounded-full mr-2">
                    <FaChartLine className="h-4 w-4" />
                  </span>
                  Real-time inventory tracking
                </li>
                <li className="flex items-center">
                  <span className="bg-green-100 text-green-800 p-1 rounded-full mr-2">
                    <FaTools className="h-4 w-4" />
                  </span>
                  Equipment maintenance scheduling
                </li>
                <li className="flex items-center">
                  <span className="bg-green-100 text-green-800 p-1 rounded-full mr-2">
                    <FaUsers className="h-4 w-4" />
                  </span>
                  Team usage analytics
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Stats Section */}
      <motion.div 
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-7xl mx-auto mb-16"
      >
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">By The Numbers</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow duration-300"
            >
              <div className="text-4xl mb-2">
                {stat.icon}
              </div>
              <div className="text-5xl font-bold text-green-600 mb-2">
                {stat.displayValue || 0}+
              </div>
              <div className="text-gray-600 text-lg">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Team Section */}
      <motion.div 
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={containerVariants}
        className="max-w-7xl mx-auto bg-white rounded-xl shadow-md overflow-hidden"
      >
        <div className="md:flex flex-row-reverse">
          <motion.div 
            variants={itemVariants}
            className="md:flex-shrink-0 bg-green-600 md:w-1/3 flex items-center justify-center p-12"
          >
            <FaUsers className="h-32 w-32 text-white" />
          </motion.div>
          <motion.div 
            variants={itemVariants}
            className="p-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Team</h2>
            <p className="mt-2 text-gray-600 text-lg">
              Our dedicated team of horticulturists, arborists, and urban foresters work across Vancouver's 
              neighborhoods to maintain and enhance our urban canopy. This inventory system was developed 
              in-house to support their important work.
            </p>
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Sustainability Goals:</h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="bg-green-100 text-green-800 p-1 rounded-full mr-2">
                    ✓
                  </span>
                  Reduce equipment downtime by 30%
                </li>
                <li className="flex items-center">
                  <span className="bg-green-100 text-green-800 p-1 rounded-full mr-2">
                    ✓
                  </span>
                  Extend tool lifespan through better maintenance
                </li>
                <li className="flex items-center">
                  <span className="bg-green-100 text-green-800 p-1 rounded-full mr-2">
                    ✓
                  </span>
                  Optimize resource allocation across districts
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default About;